import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, resolveAssetUrl } from "@/lib/api";
import { ToolPageShell, StatusPill } from "@/components/AdminTools";
import VendorServicesSnapshot from "@/components/VendorServicesSnapshot";
import BasketBar from "@/components/BasketBar";
import {
  Building2,
  Globe,
  ShieldCheck,
  Mail,
  Tag,
  FileText,
  ExternalLink,
  Boxes,
  Filter,
  Star,
} from "lucide-react";

const CATS = ["all", "sleep", "metabolic", "hormone", "diagnostics", "wellness"];

export default function PortalVendors() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [subFilter, setSubFilter] = useState("all");
  const [taxonomy, setTaxonomy] = useState({});
  const [bookmarks, setBookmarks] = useState(new Set());
  const [savedOnly, setSavedOnly] = useState(false);
  const [snapshotVendor, setSnapshotVendor] = useState(null);

  useEffect(() => {
    api.get("/vendors")
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
    api.get("/taxonomy/vendor-subcategories").then((r) => setTaxonomy(r.data || {})).catch(() => {});
    api.get("/vendors/bookmarks")
      .then((r) => setBookmarks(new Set(r.data || [])))
      .catch(() => {});
    // Mount-only: api/toast/formatApiError are stable module imports.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleBookmark = async (vid) => {
    const isSaved = bookmarks.has(vid);
    // Optimistic update.
    setBookmarks((prev) => {
      const next = new Set(prev);
      if (isSaved) next.delete(vid);
      else next.add(vid);
      return next;
    });
    try {
      if (isSaved) {
        await api.delete(`/vendors/${vid}/bookmark`);
      } else {
        await api.post(`/vendors/${vid}/bookmark`);
      }
    } catch (e) {
      // Revert on failure.
      setBookmarks((prev) => {
        const next = new Set(prev);
        if (isSaved) next.add(vid);
        else next.delete(vid);
        return next;
      });
      toast.error(formatApiError(e));
    }
  };

  const visible = items.filter((v) => {
    if (v.status !== "active") return false;
    if (savedOnly && !bookmarks.has(v.id)) return false;
    if (filter !== "all" && v.category !== filter) return false;
    if (filter !== "all" && subFilter !== "all" && (v.subcategory || "") !== subFilter) return false;
    return true;
  });

  const subList =
    filter === "all"
      ? []
      : Array.from(
          new Set([
            ...(taxonomy[filter] || []),
            ...items.filter((v) => v.category === filter && v.subcategory).map((v) => v.subcategory),
          ])
        );

  const switchCategory = (c) => {
    setFilter(c);
    setSubFilter("all");
  };

  return (
    <ToolPageShell
      eyebrow="Clinic · Vendor Network"
      title="Approved vendor partners"
      intro="Browse the curated, vetted vendor partners powering your programs. Click any vendor to see their Service Snapshot — pick services across multiple vendors, then carry your selections into Program Builder to launch a new cash-pay program."
    >
      {/* Workflow steppers — make the cross-vendor flow self-evident. */}
      <div
        data-testid="portal-vendors-workflow-banner"
        className="mb-5 rounded-2xl border border-blue-100 bg-blue-50/60 p-4"
      >
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-blue-700 mb-2">
          How program building works
        </div>
        <ol className="grid sm:grid-cols-3 gap-3 text-xs text-slate-700">
          <li className="flex items-start gap-2">
            <span className="shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold">1</span>
            <span><b>Pick services</b> across one or more vendors using <span className="font-semibold">View services</span>.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold">2</span>
            <span><b>Open Program Builder</b> to set name, cadence, pricing, and submit for admin approval.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold">3</span>
            <span><b>Admin → vendors → admin</b> approve the program, then it's deployed to your portal as active.</span>
          </li>
        </ol>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="portal-vendors-filter">
        <span className="inline-flex items-center gap-1 text-xs text-slate-500 font-semibold mr-1">
          <Filter className="w-3 h-3" /> Filter
        </span>
        {CATS.map((c) => (
          <button
            key={c}
            type="button"
            data-testid={`portal-vendors-filter-${c}`}
            onClick={() => switchCategory(c)}
            className={`text-xs font-semibold capitalize rounded-full px-3 py-1.5 border ${
              filter === c ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"
            }`}
          >
            {c === "all" ? "All" : c}
          </button>
        ))}
        <button
          type="button"
          data-testid="portal-vendors-filter-saved"
          onClick={() => setSavedOnly((s) => !s)}
          className={`ml-auto inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 border ${
            savedOnly
              ? "bg-amber-500 text-white border-amber-500"
              : "bg-white text-slate-700 border-slate-200 hover:bg-amber-50 hover:border-amber-300"
          }`}
        >
          <Star className={`w-3 h-3 ${savedOnly ? "fill-white" : ""}`} />
          {savedOnly ? `Saved (${bookmarks.size})` : `Saved (${bookmarks.size})`}
        </button>
      </div>

      {filter !== "all" && subList.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-6 pl-5 border-l-2 border-slate-200" data-testid="portal-vendors-subfilter">
          <span className="inline-flex items-center gap-1 text-[11px] text-slate-400 font-semibold uppercase tracking-[0.16em] mr-1">
            Subcategory
          </span>
          <button
            type="button"
            data-testid="portal-vendors-subfilter-all"
            onClick={() => setSubFilter("all")}
            className={`text-[11px] font-semibold rounded-full px-3 py-1 border ${
              subFilter === "all" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"
            }`}
          >
            All
          </button>
          {subList.map((s) => (
            <button
              key={s}
              type="button"
              data-testid={`portal-vendors-subfilter-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              onClick={() => setSubFilter(s)}
              className={`text-[11px] font-semibold rounded-full px-3 py-1 border ${
                subFilter === s ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {filter === "all" && <div className="mb-6" />}

      {loading && <div className="text-sm text-slate-500">Loading vendor network…</div>}

      {!loading && (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="portal-vendors-grid">
          {visible.map((v) => (
            <div key={v.id} data-testid={`portal-vendor-${v.id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-start gap-3">
                <span className="w-11 h-11 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
                  {v.logo_url ? (
                    <img src={resolveAssetUrl(v.logo_url)} alt={v.name} className="w-full h-full object-cover rounded-xl" />
                  ) : (
                    <Building2 className="w-5 h-5 text-slate-700" />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-display text-base font-semibold text-slate-900 truncate">{v.name}</div>
                    <StatusPill status={v.status} />
                    <button
                      type="button"
                      data-testid={`portal-vendor-bookmark-${v.id}`}
                      onClick={() => toggleBookmark(v.id)}
                      title={bookmarks.has(v.id) ? "Remove from saved" : "Save vendor"}
                      className={`ml-auto inline-flex items-center justify-center w-7 h-7 rounded-full border transition ${
                        bookmarks.has(v.id)
                          ? "bg-amber-50 border-amber-200 text-amber-500 hover:bg-amber-100"
                          : "bg-white border-slate-200 text-slate-300 hover:text-amber-500 hover:border-amber-300"
                      }`}
                    >
                      <Star className={`w-3.5 h-3.5 ${bookmarks.has(v.id) ? "fill-amber-500" : ""}`} />
                    </button>
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-600 mt-0.5 inline-flex items-center gap-1">
                    <Tag className="w-3 h-3" /> {v.category}
                    {v.subcategory && (
                      <span className="ml-1 normal-case tracking-normal font-medium text-slate-700">
                        · {v.subcategory}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <p className="mt-3 text-sm text-slate-700 leading-relaxed line-clamp-3">{v.description}</p>
              <div className="mt-3 space-y-1.5 text-xs">
                {v.regions && (
                  <div className="text-slate-600 inline-flex items-center gap-1.5">
                    <Globe className="w-3 h-3 text-slate-400" /> {v.regions}
                  </div>
                )}
                {v.compliance_attested && (
                  <div className="text-emerald-700 inline-flex items-center gap-1.5">
                    <ShieldCheck className="w-3 h-3" /> Compliance attested
                  </div>
                )}
              </div>

              {(v.services || []).length > 0 && (
                <div className="mt-4">
                  <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400 mb-1.5 inline-flex items-center gap-1">
                    <Boxes className="w-3 h-3" /> Services & pricing
                  </div>
                  <ul className="space-y-1.5">
                    {(v.services || []).slice(0, 4).map((s, i) => (
                      <li key={s.id || `${s.name || "svc"}-${i}`} className="text-xs text-slate-700 flex items-baseline gap-2">
                        <span className="font-semibold text-slate-900">{s.name}</span>
                        <span
                          className="text-slate-700 font-semibold tabular ml-auto whitespace-nowrap"
                          title="Negotiated client bill rate (set by Nodera with the vendor)"
                        >
                          {s.wholesale_cost != null
                            ? `$${s.wholesale_cost}`
                            : "Contact for pricing"}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(v.documents || []).length > 0 && (
                <div className="mt-3">
                  <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400 mb-1.5 inline-flex items-center gap-1">
                    <FileText className="w-3 h-3" /> Documents
                  </div>
                  <ul className="flex flex-wrap gap-1.5">
                    {(v.documents || []).map((d, i) => (
                      <li key={d.url || d.label || `doc-${i}`}>
                        <a
                          href={resolveAssetUrl(d.url)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[11px] inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5"
                        >
                          {d.label} <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-2 flex-wrap">
                {(v.services || []).length > 0 ? (
                  <button
                    type="button"
                    data-testid={`portal-vendor-view-services-${v.id}`}
                    onClick={() => setSnapshotVendor(v)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-full px-3 py-1.5"
                  >
                    <Boxes className="w-3 h-3" /> View services
                    <span className="inline-flex items-center justify-center bg-blue-500 text-white rounded-full text-[10px] min-w-[1rem] h-4 px-1 font-bold">
                      {(v.services || []).length}
                    </span>
                  </button>
                ) : (
                  <span className="text-[11px] text-slate-400 italic">
                    Service catalog coming soon
                  </span>
                )}
                <a
                  href={`mailto:info@noderahealth.com?subject=${encodeURIComponent(`Intro request: ${v.name}`)}`}
                  data-testid={`portal-vendor-intro-${v.id}`}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5"
                >
                  <Mail className="w-3 h-3" /> Request intro
                </a>
                {v.website ? (
                  <a
                    href={v.website}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-auto text-[11px] font-semibold text-blue-700 hover:text-blue-900"
                  >
                    Website ↗
                  </a>
                ) : null}
              </div>
            </div>
          ))}
          {visible.length === 0 && (
            <div className="sm:col-span-2 text-center text-slate-500 py-10 rounded-2xl border border-dashed border-slate-300 bg-white">
              {savedOnly
                ? "No saved vendors yet. Tap the star on a vendor card to save it for quick access."
                : "No vendor partners in this category yet."}
            </div>
          )}
        </div>
      )}
      {/* Sticky cross-vendor basket bar — hidden when a vendor drawer is
          open so its z-40 layer doesn't sit behind the drawer's z-50 backdrop
          (which would swallow clicks). The drawer's footer has its own
          Continue button for in-drawer continuation. */}
      <BasketBar testIdPrefix="portal-vendors-basket" hidden={!!snapshotVendor} />

      <VendorServicesSnapshot
        vendor={snapshotVendor}
        onClose={() => setSnapshotVendor(null)}
      />
    </ToolPageShell>
  );
}
